import { motion } from "framer-motion";
import { PenTool, Crosshair, Code2, Megaphone, ArrowUpRight, Chrome, Infinity as InfinityIcon, Star } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const SERVICES = [
  {
    num: "01",
    icon: PenTool,
    title: "Branding & Design",
    desc: "Identity systems, art direction, and design languages engineered to be unmistakable — from monogram to full brand universe.",
    metric: "Avg. brand lift +212%",
    testid: "service-card-branding",
  },
  {
    num: "02",
    icon: Crosshair,
    title: "Digital Ads & SEO",
    desc: "Performance media and search architecture tuned for compounding returns, not vanity impressions.",
    metric: "Avg. 12x ROAS",
    testid: "service-card-ads-seo",
  },
  {
    num: "03",
    icon: Code2,
    title: "Web Development",
    desc: "Award-calibre websites and product experiences — obsessively fast, conversion-engineered, built to win.",
    metric: "98+ Lighthouse scores",
    testid: "service-card-web-dev",
  },
  {
    num: "04",
    icon: Megaphone,
    title: "Social Media & Content",
    desc: "Editorial content engines that turn passive audiences into communities that buy, share, and stay.",
    metric: "+340% organic reach",
    testid: "service-card-social-content",
  },
];

const PAID_MEDIA = [
  {
    icon: Chrome,
    title: "Google Ads",
    desc: "Search, Shopping, and Performance Max campaigns engineered around intent — every click accounted for.",
    metric: "8.2x avg. ROAS",
    testid: "service-card-google-ads",
  },
  {
    icon: InfinityIcon,
    title: "Meta Ads",
    desc: "Full-funnel Facebook & Instagram programs — creative testing at velocity, audiences that convert.",
    metric: "-38% avg. cost per acquisition",
    testid: "service-card-meta-ads",
  },
  {
    icon: Star,
    title: "Yelp Ads",
    desc: "Local dominance for service businesses — profile optimization plus paid placement that fills the book.",
    metric: "3.4x more local leads",
    testid: "service-card-yelp-ads",
  },
];

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="relative bg-[#060B18] py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 lg:mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90 mb-4">What We Build</p>
            <h2 className="font-serif font-bold tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl text-amber-50">
              Four disciplines.
              <br />
              <span className="italic text-amber-300">One growth engine.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-slate-400">
            Every engagement draws from the full stack of the agency — strategy, identity, media, and code —
            orchestrated as a single system.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-px bg-amber-500/10 border border-amber-500/10">
          {SERVICES.map((s, i) => (
            <motion.button
              key={s.num}
              data-testid={s.testid}
              onClick={() => scrollToSection("#contact")}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-[#0A1128] p-10 lg:p-12 text-left overflow-hidden hover:bg-[#0F1A3A] transition-colors duration-500"
            >
              <span className="absolute top-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] transition-all duration-700 group-hover:w-full" />
              <div className="flex items-start justify-between">
                <s.icon size={30} strokeWidth={1.25} className="text-amber-400/80 transition-transform duration-500 group-hover:scale-110" />
                <span className="font-mono text-xs text-amber-500/50 tracking-[0.3em]">{s.num}</span>
              </div>
              <h3 className="mt-8 font-serif text-2xl lg:text-3xl font-semibold text-amber-50 flex items-center gap-3">
                {s.title}
                <ArrowUpRight
                  size={20}
                  className="text-amber-400 opacity-0 -translate-x-2 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0"
                />
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-300/80 max-w-md">{s.desc}</p>
              <p className="mt-8 inline-block rounded-full border border-amber-400/25 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300/90">
                {s.metric}
              </p>
            </motion.button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 lg:mt-20"
        >
          <div className="flex items-center gap-6 mb-8">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90">The Paid Media Desk</p>
            <span className="gold-hairline" />
          </div>
          <div className="grid sm:grid-cols-3 gap-px bg-amber-500/10 border border-amber-500/10">
            {PAID_MEDIA.map((p, i) => (
              <motion.button
                key={p.title}
                data-testid={p.testid}
                onClick={() => scrollToSection("#contact")}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group relative bg-[#0A1128] p-8 text-left overflow-hidden hover:bg-[#0F1A3A] transition-colors duration-500"
              >
                <span className="absolute top-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] transition-all duration-700 group-hover:w-full" />
                <div className="flex items-center justify-between">
                  <p.icon size={24} strokeWidth={1.25} className="text-amber-400/80 transition-transform duration-500 group-hover:scale-110" />
                  <ArrowUpRight
                    size={16}
                    className="text-amber-400 opacity-0 -translate-x-2 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0"
                  />
                </div>
                <h3 className="mt-6 font-serif text-xl lg:text-2xl font-semibold text-amber-50">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300/80">{p.desc}</p>
                <p className="mt-6 inline-block rounded-full border border-amber-400/25 px-3.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-300/90">
                  {p.metric}
                </p>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
